import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, Link } from "react-router-dom";

export default function ApplyHereRedesign() {
  const location = useLocation();
  // Safe extraction to prevent crash on direct navigation
  const positionFromState = location.state?.from || "General Application";
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Name: "",
      Email: "",
      "Mobile number": "",
      "Job Title": positionFromState
    }
  });

  const onSubmit = (data) => {
    console.log("Application Submitted:", data);
    setFormData(data);
    setSubmitted(true);
  };

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800 flex flex-col">
      {/* Hero Header */}
      <section className="relative min-h-[35vh] flex items-center justify-center pt-32 pb-12 overflow-hidden bg-brand-blue-deep shrink-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-3">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Careers Portal
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-2 leading-tight">
            Join Our Team
          </h1>
          <p className="max-w-xl mx-auto text-sm md:text-base text-slate-300 font-light leading-relaxed">
            Submit your contact information below to register your application for our active openings.
          </p>
        </div>
      </section>

      {/* Main Form Container */}
      <section className="flex-grow py-16 px-6 md:px-12 flex items-center justify-center">
        <div className="w-full max-w-lg bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-lg relative overflow-hidden">
          {/* Blue design accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-blue to-brand-teal" />

          {!submitted ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-brand-blue">Application Form</h2>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-slate-500 font-light text-sm">Position:</span>
                  <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                    {positionFromState}
                  </span>
                </div>
              </div>

              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                {/* Name */}
                <div className="flex flex-col space-y-1.5">
                  <label htmlFor="Name" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Full Name
                  </label>
                  <input
                    id="Name"
                    type="text"
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.Name ? 'border-red-400 focus:ring-red-100' : 'border-slate-200 focus:ring-brand-blue/10'} focus:border-brand-blue focus:outline-none focus:ring-4 transition-all text-sm`}
                    {...register("Name", { required: "Name is required", maxLength: 80 })}
                  />
                  {errors.Name && (
                    <span className="text-xs text-red-500 font-medium">{errors.Name.message}</span>
                  )}
                </div>

                {/* Email */}
                <div className="flex flex-col space-y-1.5">
                  <label htmlFor="Email" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    id="Email"
                    type="email"
                    placeholder="name@company.com"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.Email ? 'border-red-400 focus:ring-red-100' : 'border-slate-200 focus:ring-brand-blue/10'} focus:border-brand-blue focus:outline-none focus:ring-4 transition-all text-sm`}
                    {...register("Email", { 
                      required: "Email is required", 
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Invalid email address format"
                      }
                    })}
                  />
                  {errors.Email && (
                    <span className="text-xs text-red-500 font-medium">{errors.Email.message}</span>
                  )}
                </div>

                {/* Mobile number */}
                <div className="flex flex-col space-y-1.5">
                  <label htmlFor="MobileNumber" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Mobile Number
                  </label>
                  <input
                    id="MobileNumber"
                    type="tel"
                    placeholder="10-digit number"
                    className={`w-full px-4 py-3 rounded-xl border ${errors["Mobile number"] ? 'border-red-400 focus:ring-red-100' : 'border-slate-200 focus:ring-brand-blue/10'} focus:border-brand-blue focus:outline-none focus:ring-4 transition-all text-sm`}
                    {...register("Mobile number", {
                      required: "Mobile number is required",
                      minLength: { value: 6, message: "Too short (minimum 6 digits)" },
                      maxLength: { value: 12, message: "Too long (maximum 12 digits)" }
                    })}
                  />
                  {errors["Mobile number"] && (
                    <span className="text-xs text-red-500 font-medium">{errors["Mobile number"].message}</span>
                  )}
                </div>

                {/* Job Title */}
                <div className="flex flex-col space-y-1.5">
                  <label htmlFor="JobTitle" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Applying For Position
                  </label>
                  <input
                    id="JobTitle"
                    type="text"
                    readOnly
                    className="w-full px-4 py-3 rounded-xl border border-slate-100 bg-slate-50 text-slate-500 focus:outline-none text-sm font-semibold cursor-not-allowed"
                    {...register("Job Title", { required: true })}
                  />
                </div>

                {/* Resume Instruction */}
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2.5">
                  <span className="text-[11px] font-bold text-brand-teal uppercase tracking-wider">
                    Resume Submission Requirement
                  </span>
                  <p className="text-xs text-slate-600 font-light leading-relaxed">
                    To complete your application, please send your updated CV/Resume directly to our HR inbox:
                  </p>
                  <a 
                    href={`mailto:hr@delnovahealth.com?subject=Application: ${positionFromState}`} 
                    target="_blank" 
                    rel="noreferrer"
                    className="inline-flex items-center text-xs font-semibold text-brand-blue hover:text-brand-blue-light transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1.5 shrink-0 text-brand-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hr@delnovahealth.com</span>
                  </a>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="w-full bg-brand-blue hover:bg-brand-blue-light text-white font-bold py-3.5 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-sm mt-2"
                >
                  Submit Application Details
                </button>
              </form>
            </div>
          ) : (
            <div className="text-center space-y-6 py-6 animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-bold text-brand-blue">Thank You, {formData?.Name}!</h3>
                <p className="text-slate-500 font-light text-sm leading-relaxed max-w-sm mx-auto">
                  Your contact details have been successfully registered for the role of <span className="font-semibold text-brand-blue">{positionFromState}</span>.
                </p>
              </div>

              {/* Action Banner */}
              <div className="bg-brand-blue-deep text-white p-6 rounded-2xl space-y-3 text-left">
                <h4 className="text-xs font-bold text-brand-teal-light uppercase tracking-wider">Final Step Required</h4>
                <p className="text-xs text-slate-300 font-light leading-relaxed">
                  Please send your updated Resume/CV to our HR department to initiate the resume review phase:
                </p>
                <a 
                  href={`mailto:hr@delnovahealth.com?subject=Resume: ${formData?.Name} - ${positionFromState}`} 
                  className="w-full flex items-center justify-center bg-brand-teal hover:bg-brand-teal-light text-white font-bold py-2.5 rounded-xl transition-all duration-300 text-xs shadow-md"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Send Resume to hr@delnovahealth.com</span>
                </a>
              </div>

              <div className="pt-4">
                <Link 
                  to="/Careers"
                  className="text-xs text-slate-500 hover:text-brand-blue font-semibold transition-colors flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Careers Listings</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
